import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import { useOnWindowScroll as useOnWindowScrollOriginal } from 'rooks';

// app modules: actions
import { openModal } from 'actions/modals';

// app modules: components
import Icon from 'components/Icon';

// app modules: services
import { openWindow } from 'services/inAppBrowser';
import history from 'services/history';
import { isApp } from 'services/environment';
import { sendEvent } from 'services/analytics';

// app modules: constants
import { COLOURS, TOOLTIP_DELAY_MS } from 'constants/index';
import { MODALS } from 'constants/view';

// app modules: hooks
import useFeatureToggle from 'hooks/useFeatureToggle';

// Workaround for SSR support in rooks: https://github.com/imbhargav5/rooks/issues/559
const useOnWindowScroll = typeof window === 'object' ? useOnWindowScrollOriginal : () => {};
function VoiceAssistantLink(props) {
  const { voiceLinks, enabled } = props;
  const dispatch = useDispatch();
  const [featureEnabled] = useFeatureToggle('alexaButton');
  const delayTimerRef = useRef(null);
  const [clicked, setClicked] = useState(false);
  const [hovering, setHovering] = useState(false);
  const loggedIn = useSelector(state => state.user.logged_in);
  const canUseIntroOffer = useSelector(state => !state.user.subscription);
  const visible = voiceLinks?.length && voiceLinks.find(link => link.provider === 'alexa');

  useEffect(
    () => () => {
      clearTimeout(delayTimerRef.current);
    },
    [],
  );

  useOnWindowScroll(() => {
    setClicked(false);
    setHovering(false);
  });

  const handleSignupLinkClick = event => {
    event.preventDefault();
    if (isApp()) {
      import('components/InAppPaymentInfo/InAppPaymentInfo.jsx' /* webpackChunkName: "in-app-payment", webpackMode: "lazy" */).then(
        module => {
          dispatch(
            openModal({
              name: MODALS.IN_APP_PAYMENT,
              content: module.default,
            }),
          );
        },
      );
      setClicked(false);
    } else {
      history.push('/subscribe', {
        returnTo: history.location,
        canUseIntroOffer,
      });
    }
  };
  const handleVoiceLinkClick = () => {
    const link = voiceLinks.find(_link => _link.provider === 'alexa');
    if (!loggedIn) {
      setClicked(!clicked);
    } else if (enabled) {
      openWindow(link.url, '_system');
    } else {
      setClicked(!clicked);
    }
    sendEvent({
      event: 'VoiceAssistantLink.Click',
      provider: 'alexa',
    });
  };
  const handleTooltipClose = event => {
    if (event.target.tagName !== 'BUTTON') {
      setClicked(false);
    }
  };
  const handleTooltipMouseEnter = () => {
    delayTimerRef.current = setTimeout(() => setHovering(true), TOOLTIP_DELAY_MS);
  };
  const handleTooltipMouseLeave = () => {
    setHovering(false);
    clearTimeout(delayTimerRef.current);
  };
  let tooltipContents;
  if (clicked) {
    tooltipContents = (
      <p>
        ckbk Premium Members can send selected recipes to Alexa.{' '}
        <button onClick={handleSignupLinkClick} className="link link--energized">
          {canUseIntroOffer ? 'Sign up for a free 14 day trial' : 'Sign up now'}
        </button>
      </p>
    );
  } else {
    tooltipContents = <p>Send to Alexa</p>;
  }

  if (visible && featureEnabled) {
    return (
      <ClickAwayListener onClickAway={handleTooltipClose}>
        <Tooltip
          open={hovering || clicked}
          onClose={handleTooltipClose}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
          title={tooltipContents}
          arrow
          placement="bottom"
          interactive
          disableFocusListener
          disableHoverListener
          disableTouchListener
          TransitionProps={{
            timeout: {
              exit: 0,
            },
          }}
        >
          <span>
            <Button
              aria-label="Send to Alexa"
              color="primary"
              size="medium"
              onClick={handleVoiceLinkClick}
            >
              <Icon fill={COLOURS.ENERGIZED} name="voice" height="20" width="20" />
            </Button>
          </span>
        </Tooltip>
      </ClickAwayListener>
    );
  }
  return null;
}

VoiceAssistantLink.propTypes = {
  enabled: PropTypes.bool,
  voiceLinks: PropTypes.arrayOf(
    PropTypes.shape({ provider: PropTypes.string, url: PropTypes.string }),
  ),
};

VoiceAssistantLink.defaultProps = {
  enabled: false,
  voiceLinks: [],
};

export default VoiceAssistantLink;
